/* eslint-disable */

export enum EntityType {
  ENTITY_TYPE_UNSPECIFIED = "ENTITY_TYPE_UNSPECIFIED",
  ENTITY_TYPE_CASH_CUSTOMER = "ENTITY_TYPE_CASH_CUSTOMER",
  ENTITY_TYPE_SQ_MERCHANT = "ENTITY_TYPE_SQ_MERCHANT",
  ENTITY_TYPE_SQ_ANONYMOUS_VISITOR = "ENTITY_TYPE_SQ_ANONYMOUS_VISITOR",
  ENTITY_TYPE_SQ_BUYER = "ENTITY_TYPE_SQ_BUYER",
  ENTITY_TYPE_DROPPED_EVENTS = "ENTITY_TYPE_DROPPED_EVENTS",
  ENTITY_TYPE_SQ_PERSON = "ENTITY_TYPE_SQ_PERSON",
}

export enum JourneyOutcome {
  /**
   * JOURNEY_OUTCOME_UNSPECIFIED - This value should not be returned to client. If it is, the journey will not be ended,
   * because it doesn't know what outcome to use.
   */
  JOURNEY_OUTCOME_UNSPECIFIED = "JOURNEY_OUTCOME_UNSPECIFIED",
  /** JOURNEY_OUTCOME_SUCCEEDED - The journey was considered successful, i.e. the user was able to achieve their goal */
  JOURNEY_OUTCOME_SUCCEEDED = "JOURNEY_OUTCOME_SUCCEEDED",
  /** JOURNEY_OUTCOME_FAILED - The user was unable to complete the task for some reason. */
  JOURNEY_OUTCOME_FAILED = "JOURNEY_OUTCOME_FAILED",
  /**
   * JOURNEY_OUTCOME_USER_CANCELED - The journey was canceled by the user. This outcome alone is not enough to determine
   * whether the user experience was good or not. If the user canceled the journey because
   * their customer walked away from the sale, then there was nothing wrong with the user
   * experience. However, if they canceled because the app was responding slowly, then that
   * is a bad user experience. Thus, this outcome must be paired with frustrationSignals to
   * understand the quality of the user experience.
   */
  JOURNEY_OUTCOME_USER_CANCELED = "JOURNEY_OUTCOME_USER_CANCELED",
  /** JOURNEY_OUTCOME_PROCESS_CRASHED - The client application crashed before the user could complete their journey. */
  JOURNEY_OUTCOME_PROCESS_CRASHED = "JOURNEY_OUTCOME_PROCESS_CRASHED",
  /**
   * JOURNEY_OUTCOME_PROCESS_KILLED - The client application was killed for some unknown reason before the user could complete their
   * journey. It could be due to lost power, a system restart by the user, or even an app crash.
   */
  JOURNEY_OUTCOME_PROCESS_KILLED = "JOURNEY_OUTCOME_PROCESS_KILLED",
  /**
   * JOURNEY_OUTCOME_TERMINATED - The journey was automatically terminated by the client library, usually due to a
   * misconfiguration of the journey. The terminationReason field gives more details about why
   * the journey was terminated.
   */
  JOURNEY_OUTCOME_TERMINATED = "JOURNEY_OUTCOME_TERMINATED",
}

export enum JourneyTerminationReason {
  JOURNEY_TERMINATION_REASON_UNSPECIFIED = "JOURNEY_TERMINATION_REASON_UNSPECIFIED",
  /**
   * JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_ACTIVE_USER - User journeys automatically time out after a certain amount of time in order to ensure that
   * the journey eventually ends, even if the journey developer forgets to mark it as complete.
   * TIMEOUT_WITH_ACTIVE_USER indicates that the user was still active when the journey timed out.
   * This is an indication that the journey developer missed an "off-ramp" while tracking the
   * journey. This means that they did not mark the journey as complete when they should have, and
   * the user continued on to other parts of the app.
   */
  JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_ACTIVE_USER = "JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_ACTIVE_USER",
  /**
   * JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_IDLE_USER - The user journey timed out and the user was not active in the app at the time of the
   * termination. This is an indication that the user walked away from the app and abandoned the
   * journey. It's possible they will come back later and complete it, but we won't know.
   */
  JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_IDLE_USER = "JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_IDLE_USER",
  /**
   * JOURNEY_TERMINATION_REASON_JOURNEY_RESTARTED - A user journey was already active when the client library was instructed to start a journey
   * with the same name. Similar to TIMEOUT_WITH_ACTIVE_USER, this is an indication that the
   * journey developer missed an "off-ramp" while tracking the journey.
   */
  JOURNEY_TERMINATION_REASON_JOURNEY_RESTARTED = "JOURNEY_TERMINATION_REASON_JOURNEY_RESTARTED",
}

/** This class contains information about a completed user journey. */
export interface RecordCompletedJourneyRequest {
  /** REQUIRED FIELD: The name of the user journey, e.g. "login" */
  journey_name?: string;
  /** REQUIRED FIELD: The unique Id of this instance of the journey */
  journey_id?: string;
  /** REQUIRED FIELD: The type of entity (i.e. customer, merchant) that performed the journey event */
  entity_type?: EntityType;
  /** The unique ID of the entity who performed the journey event (e.g. C_token) */
  entity_id?: string;
  /** REQUIRED FIELD: App token of mobile or web client */
  app_token?: string;
  /**
   * REQUIRED FIELD: The name of the platform that recorded the journey, e.g. "ios", "android", or
   * "web"
   */
  platform_name?: string;
  /** The version of the platform that recorded the journey, e.g. "16.4" */
  platform_version?: string;
  /** The model of the device that recorded the journey, e.g. "iPhone" or "X2" */
  device_model?: string;
  /** REQUIRED FIELD: The name of the application that recorded the journey, e.g. "square" */
  application_name?: string;
  /**
   * The version of the application that recorded the journey, e.g. "6.34". The version will be
   * truncated server-side to only include major and minor versions. e.g. "6.34.1" -> "6.34"
   */
  application_version?: string;
  /** The locale the journey was recorded in, e.g. "en-us" */
  locale?: string;
  /** REQUIRED FIELD: The date (in milliseconds since epoch) that the user journey was started */
  created_at?: number;
  /** The date (in milliseconds since epoch) that the user journey was ended */
  ended_at?: number;
  /** REQUIRED FIELD: The time (in ms) from the start of the journey to completion of the journey */
  duration_real_time_ms?: number;
  /** REQUIRED FIELD: The outcome of the journey */
  outcome?: JourneyOutcome;
  /**
   * If the outcome is TERMINATED, then this field explains why the journey was
   * terminated
   */
  termination_reason?: JourneyTerminationReason;
  /** The variant of the UI flow that the user took. */
  variant?: string;
  /**
   * A set of client-defined frustration signals that the user experienced during
   * their journey. A frustration signal is something that negatively impacted the user
   * experience that is definitely our fault.
   */
  frustration_signals?: string[];
  /**
   * A set of client-defined friction signals that the user experienced during their
   * journey. A friction signal is something that negatively impacted the user experience
   * that is probably due to user behavior, but could be our fault if there is a sudden
   * change in the volume of that signal.
   */
  friction_signals?: string[];
  /**
   * A set of client-defined tags. Tags are a catch-all for any additional segmentation
   * the client wants to add to the journey. Both key-value and key-only tags are supported.
   * Key-value tags should be formatted as "key:value".
   * "key:value" will be emitted to Datadog as key -> value
   * "key" will be emitted to Datadog as key -> "true"
   */
  tags?: string[];
  /** REQUIRED FIELD: Id of client-side session */
  session_id?: string;
}

export interface RecordCompletedJourneyResponse {
  message?: string;
}

export interface RecordCompletedJourneyBatchResponse {
  message?: string;
}

/** Request message for batch recording of completed journeys. */
export interface RecordCompletedJourneyBatchRequest {
  /** Collection of journey requests to process */
  requests?: RecordCompletedJourneyRequest[];
}

/** A collection of signals that can be emitted by the server at any time during a journey. */
export interface JourneySignals {
  /**
   * Friction signals will be encoded as "possibly bad events" but not necessarily. Friction signals are
   * expected events but can indicate a problem at certain volumes. Examples of proposed friction is things
   * like card declines and 4xx from server.
   *
   * They're free form strings but the case should be kebab-case.
   */
  frictions?: string[];
  /**
   * Frustration signals will be encoded to "known bad events". Things that are known bad user experiences
   * that we need to form tight SLOs around. These can be non-fatal but are unexpected or undesired events.
   * For instance, a 5xx could be retried and succeed thus resulting in a successful outcome,
   * but it's a known bad thing and we want to trend to zero known bad things over time.
   *
   * They're free form strings but the case should be kebab-case.
   */
  frustrations?: string[];
}

export interface BlockUserJourneysService {
  RecordCompletedJourney(request: RecordCompletedJourneyRequest): Promise<RecordCompletedJourneyResponse>;
  RecordCompletedJourneyBatch(
    request: RecordCompletedJourneyBatchRequest,
  ): Promise<RecordCompletedJourneyBatchResponse>;
}
